import React, { useId } from 'react';
import s from './Field.module.css';

// A labelled form control: label, the control itself, and either a hint or an
// error under it — never both, because a hint under a red message reads as part
// of the error.
//
// ── The label labels the control, without the caller arranging it (#383) ────
//
// The `<label>` is a sibling of the control, so the association is
// `htmlFor`/`id`. Rather than every caller pairing the two, `useId()` mints an
// id and it is cloned onto the child, so `<Field label="…"><input …/></Field>`
// is associated without asking: otherwise a label would be announced as
// nothing, would not focus its control, and `getByLabelText` could not find it.
//
//   * An explicit `htmlFor` wins: a caller that pairs `htmlFor` with an `id`
//     keeps what it wrote, and one that passed `htmlFor` alone gets that id
//     put on the control.
//   * A child that already carries an `id` keeps it, and the label points at
//     that.
//   * Only a labelable element is touched. `for` on a `<div>` names nothing,
//     so a `Field` wrapping a radio group, a `PhoneField` or any other
//     multi-control child is left as it was; those want a `fieldset`/`legend`
//     or `aria-labelledby` (#383).
//
// The list is HTML's: the elements a `<label for>` may name.
const LABELABLE = new Set([
  'input', 'select', 'textarea', 'button', 'meter', 'output', 'progress',
]);

export default function Field({
  label,
  htmlFor,
  children,
  error,
  hint,
  required,
  style,
}) {
  // Called unconditionally, because it is a hook. The id costs nothing when the
  // child turns out to be something this must not touch.
  const generated = useId();

  // One child, and an element rather than a string or a list. Anything else is
  // a `Field` whose content this cannot reason about, and it is passed through
  // untouched. A component that puts the `id` it is handed on its own input
  // says so with a static `labelable` — the Region's date, time and amount
  // boxes (RegionInputs.jsx) — and is labelled as that input would be.
  const only = React.isValidElement(children) ? children : null;
  const labelable = Boolean(only) && (
    (typeof only.type === 'string' && LABELABLE.has(only.type)) || only.type?.labelable === true
  );
  const childId = only?.props?.id;

  const id = htmlFor || childId || (labelable ? generated : undefined);
  const control = labelable && !childId && id
    ? React.cloneElement(only, { id })
    : children;
  // Without a labelable child and without an explicit `htmlFor` there is
  // nothing honest to point at, and an id naming no element is worse than none.
  const labelFor = (labelable || htmlFor) ? id : undefined;

  return (
    <div className={s.field} style={style}>
      {label ? (
        <label className={s.label} htmlFor={labelFor}>
          {label}
          {required ? <span className={s.required}>*</span> : null}
        </label>
      ) : null}
      {control}
      {hint && !error ? <p className={s.hint}>{hint}</p> : null}
      {error ? <p className={s.error}>{error}</p> : null}
    </div>
  );
}
